import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';

import {overridePurpose} from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as BenchlingUtil from 'BenchlingUtil';
import {
  ProcessExecutionType,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery, {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {getEnabledProcessExecutionTypesForPlatformFeature} from 'processes/ProcessConstants';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {
  ProcessTaskSchemasPageQuery,
  ProcessTaskSchemasPageQuery_processTaskSchemasSupportingPlatformFeature_ProcessTaskSchemaNode as ProcessTaskSchemasListingSchema,
} from 'processes/settings/__generated/ProcessTaskSchemasPageQuery';
import ProcessTaskSchemaExecutionTypeDropdown from 'processes/tables/ProcessTaskSchemaExecutionTypeDropdown';
import {canConfigureProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import SchemasListing from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ORANGE_DARK} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {withLoadingScreen} from 'util/Promises';

import isSchemaExecutionTemplateUnpublished from './isSchemaExecutionTemplateUnpublished';

interface ProcessTaskSchemasListingCommonProps {
  areOrganizationSchemasCreatable: boolean;
}

interface ProcessTaskSchemasListingPropsWithoutSchema extends ProcessTaskSchemasListingCommonProps {
  processTaskSchemas: null;
}

interface ProcessTaskSchemasListingPropsWithSchema extends ProcessTaskSchemasListingCommonProps {
  processTaskSchemas: Array<ProcessTaskSchemasListingSchema>;
  refetch: () => void;
}

type ProcessTaskSchemasListingProps =
  | ProcessTaskSchemasListingPropsWithoutSchema
  | ProcessTaskSchemasListingPropsWithSchema;

export function ProcessTaskSchemasListing(props: ProcessTaskSchemasListingProps): JSX.Element {
  const [selectedExecutionTypes, setSelectedExecutionTypes] = useState(
    /* eslint-disable-next-line no-restricted-syntax */
    getEnabledProcessExecutionTypesForPlatformFeature(ProcessPlatformFeatureType.SCHEMA_CONFIG)
      .sort()
      .map((executionType) => executionType)
  );

  const handleNavigateToCreate = (): void => {
    BenchlingUtil.navigate(null, ProcessSchemaDAO.createProcessTaskSchemaURL());
  };

  const bulkArchiveProcessSchemas = async (ids: Array<string>, purpose: string): Promise<void> => {
    try {
      await withLoadingScreen(
        ProcessSchemaDAO.bulkArchive(ids, overridePurpose(purpose, ids)),
        'Archiving...'
      );
      notifications.success('Items successfully archived.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    (props as ProcessTaskSchemasListingPropsWithSchema).refetch();
  };

  const bulkUnarchiveProcessSchemas = async (ids: Array<string>): Promise<void> => {
    try {
      await ProcessSchemaDAO.bulkUnarchive(ids);
      notifications.success('Items successfully unarchived.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    (props as ProcessTaskSchemasListingPropsWithSchema).refetch();
  };

  const handleChangeExecutionTypeFilters = (selectedExecutionTypes: Array<ProcessExecutionType>): void => {
    setSelectedExecutionTypes(selectedExecutionTypes);
  };

  const filterForExecutionTypes = (
    schemas: Array<ProcessTaskSchemasListingSchema>
  ): Array<ProcessTaskSchemasListingSchema> =>
    schemas.filter((item) => selectedExecutionTypes.includes(item.executionType));

  let showOrganizationColumn = false;
  if (props.processTaskSchemas != null) {
    showOrganizationColumn = props.processTaskSchemas.some(
      (schema) => schema.organization.id !== props.processTaskSchemas[0].organization.id
    );
  }

  const columnsToShow = showOrganizationColumn
    ? [
        {name: 'Schemas', width: 6},
        {name: 'Execution Type', width: 2},
        {name: 'Organization', width: 4},
      ]
    : [
        {name: 'Schemas', width: 8},
        {name: 'Execution Type', width: 4},
      ];

  BenchlingUtil.setWindowTitle('Workflow Schemas');
  return (
    <SchemasListing
      schemas={props.processTaskSchemas}
      schemaType={SCHEMA_TYPES.PROCESS}
      columnHeaders={columnsToShow}
      customSearchControls={
        <ProcessTaskSchemaExecutionTypeDropdown
          onChangeExecutionTypes={handleChangeExecutionTypeFilters}
          selectedExecutionTypes={selectedExecutionTypes}
        />
      }
      customFilterCallback={filterForExecutionTypes}
      navigateToCreate={handleNavigateToCreate}
      urlForSchema={(schemaId: string) => ProcessSchemaDAO.processTaskSchemaURL(schemaId)}
      archiveSchemas={bulkArchiveProcessSchemas}
      unarchiveSchemas={bulkUnarchiveProcessSchemas}
      isWritable={props.areOrganizationSchemasCreatable}
      mapSchemaToRowValue={(schema) => [
        <span className="u-small-spacing-left">
          {isSchemaExecutionTemplateUnpublished(schema) && (
            <Tooltip
              text="The execution template for this task schema isn't published."
              placement={FloatingPlacement.TOP}
            >
              <BenchlingIcon className={css(styles.warningIcon)} icon="warning" />
            </Tooltip>
          )}
          {schema.usesArchivedTaskSchema && (
            <Tooltip
              text="This flowchart uses one or more archived task schemas."
              placement={FloatingPlacement.TOP}
            >
              <BenchlingIcon className={css(styles.warningIcon)} icon="warning" />
            </Tooltip>
          )}
          {schema.name}
          <ArchiveRecordLabel item={schema} />
        </span>,
        processExecutionTypeDisplayName(schema.clientOrigin, schema.executionType),
        ...(showOrganizationColumn ? [schema.organization.name] : []),
      ]}
      getBannerInfo={(filteredSchemas) => {
        const numSchemasWithUnpublishedExecutionTemplate =
          filteredSchemas?.filter((schema) => isSchemaExecutionTemplateUnpublished(schema)).length ?? 0;

        const numSchemasWithArchivedTaskSchema =
          filteredSchemas?.filter((schema) => schema.usesArchivedTaskSchema).length ?? 0;

        if (numSchemasWithUnpublishedExecutionTemplate === 0 && numSchemasWithArchivedTaskSchema === 0) {
          return null;
        }

        const bannerTitle = `${numSchemasWithUnpublishedExecutionTemplate + numSchemasWithArchivedTaskSchema} schemas have configuration issues.`;

        const supportingInfo = 'Fix them so users can create and run tasks.';
        return {
          text: bannerTitle,
          supportingInfo,
          bannerType: InlineBannerType.WARNING,
        };
      }}
    />
  );
}

export const PROCESS_TASK_SCHEMAS_PAGE_QUERY: DocumentNode = gql`
  query ProcessTaskSchemasPageQuery($platformFeature: ProcessPlatformFeatureType!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: $platformFeature) {
      id
      name
      archiveRecord {
        purpose
      }
      executionType
      clientOrigin
      organization
      entryTemplate {
        id
        currentPublishingRecord {
          ... on PublishingRecordNode {
            id
            publishingStatus
          }
          ...InaccessibleInfoGraphQL
        }
      }
      usesArchivedTaskSchema
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function ProcessTaskSchemasPage(): JSX.Element | null {
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;
  return (
    <BenchlingQuery<ProcessTaskSchemasPageQuery>
      query={PROCESS_TASK_SCHEMAS_PAGE_QUERY}
      variables={{
        platformFeature: ProcessPlatformFeatureType.SCHEMA_CONFIG,
      }}
      renderLoading={() => (
        <ProcessTaskSchemasListing
          processTaskSchemas={null}
          areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
        />
      )}
      fetchPolicy="cache-and-network"
      renderCachedDataWhenRefetching={true}
    >
      {(data, {refetch}) => (
        <ProcessTaskSchemasListing
          processTaskSchemas={data.processTaskSchemasSupportingPlatformFeature.filter((schema) =>
            canConfigureProcessTaskSchema(schema)
          )}
          areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
          refetch={async () => {
            await refetch();
          }}
        />
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  warningIcon: {
    color: ORANGE_DARK,
    marginRight: 8,
  },
});
